import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton } from "@/components/marketing";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const TEAL = "#22D3C5";
const VIDEO = "https://customer-assets.emergentagent.com/job_pointtaken-ai/artifacts/alqk0u08_Claire-Aicom-Avatar.mp4";
const POSTER = "https://customer-assets.emergentagent.com/job_pointtaken-ai/artifacts/fdkf3ram_Claire-bred.png";

export default function GuideThankYou() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [booked, setBooked] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Udfyld venligst navn og email");
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source_page: "15-min samtale (guide tak-side)" });
      setBooked(true);
      toast.success("Tak! Vi kontakter dig hurtigst muligt.");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden" data-testid="guide-thankyou-page">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 pt-24 pb-10 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
          className="inline-flex h-14 w-14 rounded-2xl items-center justify-center mb-7" style={{ backgroundColor: "rgba(34,211,197,0.15)" }}>
          <CheckCircle2 className="h-7 w-7" style={{ color: TEAL }} strokeWidth={1.75} />
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-balance leading-[1.08]">
          Sådan! Din guide er på vej til din inbox
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="balance-mobile mt-6 text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          Men mens du venter... skal du hilse på Claire - vores AI-assistent. Hun har noget på hjerte, du virkelig bør høre.
        </motion.p>
      </section>

      {/* Video */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
          className="rounded-2xl border border-border bg-black overflow-hidden shadow-xl shadow-black/10 max-w-2xl mx-auto">
          <video src={VIDEO} poster={POSTER} controls autoPlay playsInline preload="metadata"
            className="w-full h-auto block" data-testid="thankyou-video">
            Din browser understøtter ikke afspilning af video.
          </video>
        </motion.div>
      </section>

      {/* CTA - 15 min samtale */}
      <section className="relative z-10 border-y border-border bg-card">
        <div className="max-w-2xl mx-auto px-6 py-20">
          {booked ? (
            <div className="text-center" data-testid="thankyou-booked">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4" style={{ color: TEAL }} strokeWidth={1.5} />
              <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">Tak - vi vender tilbage hurtigst muligt</h2>
              <p className="text-muted-foreground mt-3">Vi glæder os til at tale med dig om, hvordan I kommer godt ind i AI-æraen.</p>
            </div>
          ) : (
            <>
              <div className="text-center max-w-xl mx-auto">
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Uforpligtende samtale</p>
                <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-balance leading-snug">
                  Book en 15 minutters uforpligtende samtale og find ud af, om din virksomhed er klar til at træde ind i <span style={{ color: TEAL }}>AI-æraen</span>
                </h2>
              </div>
              <form onSubmit={submit} className="mt-8 space-y-4 max-w-md mx-auto" data-testid="thankyou-form">
                <div className="space-y-2">
                  <Label htmlFor="ty-name">Navn</Label>
                  <Input id="ty-name" value={form.name} onChange={set("name")} placeholder="Dit navn" data-testid="thankyou-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ty-email">Email</Label>
                  <Input id="ty-email" type="email" value={form.email} onChange={set("email")} placeholder="dig@virksomhed.dk" data-testid="thankyou-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ty-phone">Telefon</Label>
                  <Input id="ty-phone" value={form.phone} onChange={set("phone")} placeholder="+45 ..." data-testid="thankyou-phone" />
                </div>
                <Button type="submit" className="w-full h-11" disabled={loading} data-testid="thankyou-submit">
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Book samtale
                </Button>
              </form>
            </>
          )}
        </div>
      </section>

      {/* CTA til Aicom AI */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="inline-flex h-12 w-12 rounded-2xl bg-foreground items-center justify-center mb-6">
          <Sparkles className="h-6 w-6 text-background" strokeWidth={1.5} />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">
          Mød din nye digitale <span style={{ color: TEAL }}>kommunikationsrådgiver</span>
        </h2>
        <p className="balance-mobile text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
          Aicom AI hjælper jer med at være synlige, genkendelige og relevante - uden at starte fra nul hver gang.
        </p>
        <div className="mt-8 flex justify-center">
          <MButton to="/losninger/aicom-ai" size="lg" data-testid="thankyou-aicom-btn">Se Aicom AI <ArrowRight className="h-4 w-4" /></MButton>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
